/**
 * Solution utilities - shared functions for solution components
 * Consolidates duplicated code from SolutionDisplay, SolutionView, SolutionBuilderEnhanced
 */

import { SOLUTION_TYPES, SOLUTION_TYPE_STYLES } from '../constants/solutionTypes';

/**
 * Get the color associated with a solution type
 * @param {string} type - The solution type (e.g., 'health', 'financial', 'legal', 'government')
 * @returns {string} Hex color code
 */
export const getSolutionColor = (type) => {
    // Normalize type to lowercase for comparison
    const normalizedType = (type || '').toLowerCase().trim();

    // Map various type formats to standardized keys
    const typeMapping = {
        // Sector types (from constants)
        'health': SOLUTION_TYPES.HEALTH,
        'financial': SOLUTION_TYPES.FINANCIAL,
        'legal': SOLUTION_TYPES.LEGAL,
        'government': SOLUTION_TYPES.GOVERNMENT,
        // Full label variants
        'health solution': SOLUTION_TYPES.HEALTH,
        'financial solution': SOLUTION_TYPES.FINANCIAL,
        'legal solution': SOLUTION_TYPES.LEGAL,
        'government solution': SOLUTION_TYPES.GOVERNMENT,
        // Legacy mode types
        'proposal': 'proposal',
        'idea': 'idea',
        'question': 'question',
        'issue': 'issue'
    };

    // Color definitions
    const colors = {
        // Sector colors (from SOLUTION_TYPE_STYLES)
        [SOLUTION_TYPES.HEALTH]: '#4CAF50',      // green
        [SOLUTION_TYPES.FINANCIAL]: '#2196F3',   // blue
        [SOLUTION_TYPES.LEGAL]: '#9C27B0',       // purple
        [SOLUTION_TYPES.GOVERNMENT]: '#FF9800',  // orange
        // Legacy mode colors
        'proposal': '#4CAF50',
        'idea': '#2196F3',
        'question': '#FFC107',
        'issue': '#F44336',
        // Default
        'default': '#6B7280'
    };

    const mappedType = typeMapping[normalizedType];
    return colors[mappedType] || colors.default;
};

/**
 * Get the display label for a solution type
 * @param {string} type - The solution type
 * @returns {string} Human-readable label
 */
export const getSolutionTypeLabel = (type) => {
    const normalizedType = (type || '').toLowerCase().trim();

    const labels = {
        'health': 'Health Solution',
        'financial': 'Financial Solution',
        'legal': 'Legal Solution',
        'government': 'Government Solution',
        'proposal': 'Proposal',
        'idea': 'Idea',
        'question': 'Question',
        'issue': 'Issue'
    };

    return labels[normalizedType] || 'Solution';
};

/**
 * Get full style configuration for a solution type
 * Uses constants from solutionTypes.js when available
 * @param {string} type - The solution type
 * @returns {Object} Style configuration with color, bgColor, borderColor, label
 */
export const getSolutionTypeStyle = (type) => {
    const normalizedType = (type || '').toLowerCase().trim();

    // Check if it's a sector type with full styles defined
    const sectorType = Object.values(SOLUTION_TYPES).find(t => t === normalizedType);
    if (sectorType && SOLUTION_TYPE_STYLES[sectorType]) {
        return SOLUTION_TYPE_STYLES[sectorType];
    }

    // Fallback for legacy types
    const color = getSolutionColor(type);
    return {
        color,
        bgColor: `${color}15`, // 15% opacity
        borderColor: `${color}40`, // 40% opacity
        label: getSolutionTypeLabel(type)
    };
};
